/**
 * Pricing Summary Card
 * Displays current pricing breakdown based on enabled features
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function PricingSummaryCard({ pricing, hasUnsavedChanges }) {
  if (!pricing) return null;

  const getTierColor = (tier) => {
    switch (tier) {
      case 'ai_only':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'smart_hotel':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'web3_hotel':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'ai_only':
        return 'AI Only';
      case 'smart_hotel':
        return 'Smart Hotel';
      case 'web3_hotel':
        return 'Web3 Hotel';
      default:
        return 'Custom';
    }
  };

  return (
    <Card 
      className="sticky top-6"
      data-testid="feature-pricing-summary"
    >
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-primary" strokeWidth={1.75} />
          </div>
          <div>
            <CardTitle className="text-lg heading-font">Pricing Summary</CardTitle>
            <CardDescription className="text-xs">
              {hasUnsavedChanges ? 'Preview (unsaved)' : 'Current plan'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Tier Badge */}
        <div>
          <Badge 
            variant="outline" 
            className={`${getTierColor(pricing.tier)} text-sm px-3 py-1`}
          >
            {getTierLabel(pricing.tier)}
          </Badge>
        </div>

        <Separator />

        {/* Pricing Breakdown */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Base Platform Fee</span>
            <span className="mono font-medium">${pricing.base_price}</span>
          </div>

          {pricing.enabled_features && pricing.enabled_features.length > 0 && (
            <div className="space-y-2">
              {pricing.enabled_features.map((feature, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between text-sm pl-4 border-l-2 border-primary/20"
                >
                  <span className="text-muted-foreground text-xs">{feature.feature}</span>
                  <span className="mono text-xs font-medium">+${feature.cost}</span>
                </div>
              ))}
            </div>
          )}

          <Separator />

          <div className="flex items-center justify-between text-base font-semibold">
            <span className="heading-font">Total Monthly</span>
            <span className="mono text-xl heading-font" data-testid="total-monthly-price">
              ${pricing.total_monthly}
            </span>
          </div>
        </div>

        {/* Gas Fees Notice */}
        {pricing.gas_fees_note && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-xs text-amber-800">
              {pricing.gas_fees_note}
            </AlertDescription>
          </Alert>
        )}

        {/* Unsaved Changes Warning */}
        {hasUnsavedChanges && (
          <Alert>
            <TrendingUp className="h-4 w-4" />
            <AlertDescription className="text-xs">
              This is a preview. Click <strong>Save Changes</strong> to activate.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
